import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import * as Highcharts from 'highcharts';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  DATE_AGGREGATION,
  type HighchartsDateTimeFormatType,
} from 'lumen/utils/constants/general-constants';
import formatDate from 'lumen/utils/format-local-date';

interface HotelBenchmarkInsightTablesCellsCellDateSignature {
  Args: {
    column: {
      params: {
        dateAggregation: keyof HighchartsDateTimeFormatType;
        selectedRange: {
          end: Dayjs;
          start: Dayjs;
        };
      };
    };
    row: {
      isTotalRow: boolean;
    };
    value: Dayjs;
  };
}

export default class HotelBenchmarkInsightTablesCellsCellDate extends Component<HotelBenchmarkInsightTablesCellsCellDateSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    const { value } = this.args;

    if (!value) {
      return this.intl.t('general.missing');
    }

    const { dateAggregation } = this.args.column.params;
    const labelFormat = this.dates.highchartsLabelFormats[dateAggregation];

    if (dateAggregation === DATE_AGGREGATION.WEEK) {
      const dateFormatted = Highcharts.dateFormat(
        labelFormat,
        this.hotelAndMarketService.toLocalUnixTime(value.unix()),
      );

      return this.intl.t('general.week_of', {
        date: dateFormatted,
      });
    }

    return formatDate(value);
  }

  get isCurrentDayOrWeek(): boolean {
    return dayjs(this.args.value).isSame(
      this.hotelAndMarketService.getLocalTime(),
      this.args.column.params.dateAggregation,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTotalRow() {
    return this.args?.row?.isTotalRow;
  }

  get showWarningForWeek(): boolean {
    if (
      this.args.value &&
      this.args.column.params.dateAggregation === DATE_AGGREGATION.WEEK
    ) {
      const valueLocalised = this.hotelAndMarketService.toLocalTime(
        this.args.value.toString(),
      );
      return (
        valueLocalised.isBefore(
          this.args.column.params.selectedRange.start,
          'day',
        ) ||
        valueLocalised
          .add(6, 'days')
          .isAfter(this.args.column.params.selectedRange.end, 'day')
      );
    }
    return false;
  }

  <template>
    <span
      class="flex flex-row items-center
        {{if (or this.isTotalRow this.isCurrentDayOrWeek) 'text-200--bold'}}"
    >
      {{#if this.isTotalRow}}
        {{t "benchmark_insight.tables.detailed_view.row.total"}}
      {{else}}
        {{this.formattedValue}}
      {{/if}}
      {{#if this.showWarningForWeek}}
        <span class="ml-100">
          <GeneralIconWithTooltip
            @color="warning"
            @iconName="warning"
            @size="small"
            @tooltipContent={{t "general.incomplete_period_extra"}}
            @tooltipTitle={{t "general.incomplete_period_detail.week"}}
          />
        </span>
      {{/if}}
    </span>
  </template>
}
